import { forwardRef } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { FormButton } from "@/components/form/FormButton";
import { GRAPH_SCALE_CONTAINER_CLASS } from "@/components/graph/constants";
import { GraphZoomButtons } from "@/components/graph/GraphZoomButtons";
import { insertGraph } from "@/components/graph/insertGraph";
import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import type { GGBApi } from "@/components/graph/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { GraphNodeModel } from "@/models/nodes/GraphNodeModel";
import type { NodeModel } from "@/models/nodes/types";

type Props = {
  doc: DocumentModel;
  expression: string;
  expressions: NodeModel[][];
  api: GGBApi | null;
  onInsert?: (node: GraphNodeModel) => void;
  hidden: boolean;
};

export const Graph = observer(
  forwardRef<HTMLDivElement, Props>(function Graph(
    { doc, expression, expressions, api, onInsert, hidden },
    ref,
  ) {
    const t = useTranslations();

    const handleInsertGraph = () => {
      const graphNode = insertGraph(doc, expressions);

      if (graphNode) {
        onInsert?.(graphNode);
      }
    };

    return (
      <div className={hidden ? "max-h-0 overflow-hidden invisible" : "w-full"}>
        <span className="font-mono text-xxxs cursor-text select-none uppercase tracking-tight text-gray-500  pl-1">
          {t("sidebar.activeNode.graph.title.graph")}
        </span>
        <div
          className={twMerge(
            GRAPH_SCALE_CONTAINER_CLASS,
            "relative mb-2 shadow-block dark:shadow-block-inverted overflow-hidden rounded-6 h-[200px]",
          )}
        >
          <figure
            ref={ref}
            aria-hidden={hidden}
            className="corca-geogebra-graph absolute inset-0"
            aria-label={t("doc.graph.figureLabel", { expression })}
          />
          {!hidden && api && (
            <GraphZoomButtons
              className="absolute bottom-1 right-1"
              onZoomIn={() => api.evalCommand("ZoomIn(1.5)")}
              onZoomOut={() => api.evalCommand("ZoomOut(1.5)")}
            />
          )}
        </div>
        {!hidden && (
          <FormButton
            onClick={handleInsertGraph}
            variant="outline"
            className="flex items-center justify-center w-full h-8.5 border-gray-300 dark:border-gray-700 rounded-6"
          >
            <Icon name="Import" className="mr-1" />
            {t("sidebar.activeNode.graph.actions.insertGraph")}
          </FormButton>
        )}
        {/* <div className="flex flex-col gap-2">
        {Object.entries(rangedValues).map(([name, value]) => {
          return (
            <div className="flex gap-1" key={`${name}-${value}`}>
              <div>
                {name} = {value}
              </div>
              <input
                key={name}
                type="range"
                defaultValue={value.toString()}
                min={(value - 5).toString()}
                max={(value + 5).toString()}
                step="0.1"
                onChange={(e) => {
                  api?.setValue(name, e.target.value);
                }}
              />
            </div>
          );
        })}
      </div> */}
      </div>
    );
  }),
);
